import axios from 'axios';
import { getApiUrl } from '../../../config/api';

export class EditMenuController {
  constructor() {
    this.menu = {
      MenuId: '',
      Nombre: '',
      MenuPadre: '',
      Orden: 0
    };
    this.parentMenus = [];
    this.loading = false;
    this.saving = false;
    this.error = null;
    this.originalMenu = null;
  }

  /**
   * Inicializa el controlador cargando los datos del menú y menús padre
   * @param {string|number} menuId - ID del menú a editar
   */
  async init(menuId) {
    if (!menuId) {
      this.error = 'ID de menú no proporcionado';
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      await Promise.all([
        this.fetchMenuData(menuId),
        this.fetchParentMenus()
      ]);
    } catch (error) {
      console.error('Error al inicializar:', error);
      this.error = 'Error al cargar los datos del menú';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Obtiene los datos del menú específico
   * @param {string|number} menuId - ID del menú
   */
  async fetchMenuData(menuId) {
    try {
      const response = await axios.post(
        getApiUrl('/api/Menu/lectura'), 
        { MenuId: menuId },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        this.menu = {
          MenuId: response.data.MenuId || '',
          Nombre: response.data.Nombre || '',
          MenuPadre: response.data.MenuPadre || '',
          Orden: response.data.Orden || 0
        };
        
        // Guardar copia original para comparaciones
        this.originalMenu = { ...this.menu };
      } else {
        throw new Error('No se encontraron datos del menú');
      }
    } catch (error) {
      console.error('Error al cargar datos del menú:', error);
      this.error = 'No se pudieron cargar los datos del menú';
      throw error;
    }
  }

  /**
   * Obtiene la lista de menús padre disponibles
   */
  async fetchParentMenus() {
    try {
      const response = await axios.post(getApiUrl('/api/Menu/listar'));
      this.parentMenus = response.data || [];
    } catch (error) {
      console.error('Error al cargar menús padre:', error);
      // No lanzar error aquí ya que no es crítico para la edición
    }
  }

  /**
   * Valida los datos del menú antes de actualizar
   * @returns {boolean} true si los datos son válidos
   */
  validateMenu() {
    if (!this.menu.Nombre || this.menu.Nombre.trim() === '') {
      this.error = 'El nombre del menú es requerido';
      return false;
    }

    if (this.menu.Nombre.length > 100) {
      this.error = 'El nombre del menú no puede exceder 100 caracteres';
      return false;
    }

    if (this.menu.Orden < 0) {
      this.error = 'El orden debe ser un número positivo';
      return false;
    }

    if (this.menu.Orden > 9999) {
      this.error = 'El orden no puede ser mayor a 9999';
      return false;
    }

    // Validar que no se esté asignando como padre de sí mismo
    if (this.menu.MenuPadre && this.menu.MenuPadre.toString() === this.menu.MenuId.toString()) {
      this.error = 'Un menú no puede ser padre de sí mismo';
      return false;
    }

    this.error = null;
    return true;
  }

  /**
   * Prepara los datos del menú antes del envío
   */
  prepareMenuData() {
    // Limpiar espacios en blanco del nombre
    this.menu.Nombre = this.menu.Nombre.trim();
    
    // Asegurar que el orden sea un número
    this.menu.Orden = parseInt(this.menu.Orden) || 0;
    
    // Manejar MenuPadre vacío
    if (!this.menu.MenuPadre || this.menu.MenuPadre === '') {
      this.menu.MenuPadre = null;
    }
  }

  /**
   * Verifica si hay cambios en el menú
   * @returns {boolean} true si hay cambios
   */
  hasChanges() {
    if (!this.originalMenu) return true;
    
    return (
      this.menu.Nombre !== this.originalMenu.Nombre ||
      this.menu.MenuPadre !== this.originalMenu.MenuPadre ||
      this.menu.Orden !== this.originalMenu.Orden
    );
  }

  /**
   * Actualiza el menú en el servidor
   * @returns {Promise} Promise que resuelve cuando el menú es actualizado
   */
  async updateMenu() {
    if (!this.validateMenu()) {
      return Promise.reject(new Error(this.error));
    }

    if (!this.hasChanges()) {
      return {
        success: true,
        message: 'No hay cambios para guardar',
        data: this.menu
      };
    }

    this.saving = true;
    this.error = null;

    try {
      this.prepareMenuData();
      
      const response = await axios.post(getApiUrl('/api/Menu/actualizar'), this.menu);
      
      this.saving = false;
      
      // Verificar si la respuesta indica éxito
      const success = response.data && (response.data.Number > 0 || response.data.success !== false);
      
      if (success) {
        // Actualizar la copia original con los nuevos datos
        this.originalMenu = { ...this.menu };
      }
      
      return {
        success: success,
        message: response?.data?.message || 'Menú actualizado correctamente.',
        data: response.data
      };
    } catch (error) {
      console.error('Error al actualizar menú:', error);
      this.error = 'No se pudo actualizar el menú. Por favor, verifique los datos e intente nuevamente.';
      this.saving = false;
      throw error;
    }
  }

  /**
   * Resetea el menú a su estado original
   */
  resetToOriginal() {
    if (this.originalMenu) {
      this.menu = { ...this.originalMenu };
      this.error = null;
    }
  }

  /**
   * Actualiza un campo específico del menú
   * @param {string} field - Nombre del campo
   * @param {any} value - Valor del campo
   */
  updateField(field, value) {
    if (Object.prototype.hasOwnProperty.call(this.menu, field)) {
      this.menu[field] = value;
      
      // Limpiar error cuando el usuario modifica los datos
      if (this.error) {
        this.error = null;
      }
    }
  }

  /**
   * Obtiene el estado actual del menú
   * @returns {Object} Copia del objeto menú
   */
  getMenu() {
    return { ...this.menu };
  }

  /**
   * Obtiene la lista de menús padre
   * @returns {Array} Lista de menús padre
   */
  getParentMenus() {
    return [...this.parentMenus];
  }

  /**
   * Obtiene el estado de carga
   * @returns {boolean} true si está cargando
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Obtiene el estado de guardado
   * @returns {boolean} true si está guardando
   */
  isSaving() {
    return this.saving;
  }

  /**
   * Obtiene el error actual
   * @returns {string|null} Mensaje de error o null
   */
  getError() {
    return this.error;
  }

  /**
   * Limpia el error actual
   */
  clearError() {
    this.error = null;
  }

  /**
   * Obtiene información sobre el menú padre actual
   * @returns {Object|null} Información del menú padre o null
   */
  getCurrentParentInfo() {
    if (!this.menu.MenuPadre) return null;
    
    return this.parentMenus.find(parent => 
      parent.MenuId.toString() === this.menu.MenuPadre.toString()
    ) || null;
  }

  /**
   * Obtiene los menús padre disponibles (excluyendo el menú actual y sus descendientes)
   * @returns {Array} Lista filtrada de menús padre
   */
  getAvailableParentMenus() {
    if (!this.menu.MenuId) return this.parentMenus;
    
    // Filtrar el menú actual para evitar que sea padre de sí mismo
    return this.parentMenus.filter(parent => 
      parent.MenuId.toString() !== this.menu.MenuId.toString()
    );
  }
}

// Exportar una instancia por defecto para uso directo
export default new EditMenuController();